import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Product } from '../types';

export default function TemplatesTripwire() {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProduct() {
      try {
        const docRef = doc(db, 'products', 'tripwire');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() } as Product);
        }
      } catch (err) {
        console.error('Failed to load product:', err);
      } finally {
        setLoading(false);
      }
    }

    loadProduct();
  }, []);

  function handleBuyNow() {
    // Redirect directly to Paystack hosted payment page
    window.location.href = 'https://paystack.shop/pay/dealvelocity-tripwire';
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-secondary">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">Product not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-primary to-blue-900 text-white py-16">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {product.name}
          </h1>
          <p className="text-xl mb-8">
            {product.description}
          </p>
          <div className="text-5xl font-bold mb-4">
            R{product.priceZAR.toLocaleString()}
          </div>
          <p className="text-lg text-gray-200 mb-8">
            One-time payment. Instant download. Fully editable.
          </p>
          <button
            onClick={handleBuyNow}
            className="bg-white text-primary px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-100 transition-colors"
          >
            Buy Now — R{product.priceZAR}
          </button>
        </div>
      </section>

      {/* What's Included */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <h2 className="text-3xl font-bold text-primary text-center mb-12">
          What's Included
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {product.features.map((feature, index) => (
            <div key={index} className="flex items-start">
              <span className="text-green-500 text-2xl mr-3">✓</span>
              <span className="text-lg text-secondary">{feature}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-gray-50 py-16">
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-3xl font-bold text-primary text-center mb-12">
            Why This Pack?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold mb-2">Close Deals Faster</h3>
              <p className="text-secondary">
                No drafting delays. Customize and send in minutes.
              </p>
            </div>
            <div>
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="text-xl font-semibold mb-2">Protect Your Business</h3>
              <p className="text-secondary">
                Clear payment terms, scope protection, and POPIA compliance.
              </p>
            </div>
            <div>
              <div className="text-4xl mb-4">🇿🇦</div>
              <h3 className="text-xl font-semibold mb-2">SA-Specific</h3>
              <p className="text-secondary">
                Designed for South African SMEs, not generic global templates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <div className="border-2 border-green-500 rounded-lg p-8 bg-green-50">
          <h3 className="text-2xl font-bold text-green-800 mb-4">
            7-Day Money-Back Guarantee
          </h3>
          <p className="text-lg text-green-700">
            If the templates don't help you create clearer contracts or close deals faster,
            email within 7 days for a full refund. No questions asked.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary text-white py-16">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Protect Your Business?
          </h2>
          <p className="text-xl mb-8">
            R{product.priceZAR} once-off. Instant access. Zero risk.
          </p>
          <button
            onClick={handleBuyNow}
            className="bg-white text-primary px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-100 transition-colors"
          >
            Buy Now — R{product.priceZAR}
          </button>
        </div>
      </section>
    </div>
  );
}
